import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { getApiUrl } from '#lib/utils/environment';
import { WithCustomerPortalApi } from '../customerPortalApi';
import { type EntityItem, type EntityRequestItem, type PersistEntityFn } from './types';

export type UseSyncPersistedEntitiesResult = {
  entities: Record<string, EntityItem[] | undefined> | undefined;
  persistEntity: PersistEntityFn;
  loading: boolean;
  mutation: UseMutationResult<EntityRequestItem, Error, EntityRequestItem>;
} & UseQueryResult<Record<string, EntityItem[] | undefined>>;

const readSavedEntityId = (response: unknown): string | undefined => {
  if (typeof response !== 'object' || response === null || !('id' in response)) {
    return undefined;
  }

  const { id } = response;

  return typeof id === 'string' ? id : undefined;
};

const useSyncPersistedEntities: () => UseSyncPersistedEntitiesResult = WithCustomerPortalApi<
  Record<string, EntityItem[] | undefined>,
  UseSyncPersistedEntitiesResult,
  undefined
>(
  (fetchFn) => {
    const queryClient = useQueryClient();

    const query = useQuery({
      queryKey: ['persisted-entities'],
      queryFn: ({ signal }) =>
        fetchFn({
          url: getApiUrl() + '/user-entities',
          options: {
            method: 'GET',
            signal,
          },
        }),
    });

    const mutation = useMutation<EntityRequestItem, Error, EntityRequestItem>({
      mutationFn: async (item): Promise<EntityRequestItem> => {
        const response: unknown = await fetchFn({
          url: getApiUrl() + '/user-entities',
          options: {
            method: 'POST',
            body: JSON.stringify(item),
          },
        });

        // The backend assigns an id on create; later saves must reuse it to overwrite the same row
        return { ...item, id: item.id ?? readSavedEntityId(response) };
      },
      onSuccess: (item) => {
        queryClient.setQueryData<Record<string, EntityItem[] | undefined>>(
          ['persisted-entities'],
          (prevEntities) => ({
            ...(prevEntities ?? {}),
            [item.entityKey]: [
              {
                id: item.id,
                value: item.entityValue,
              },
            ],
          }),
        );
      },
    });

    const persistEntity = useCallback(
      async (item: EntityRequestItem) => mutation.mutateAsync(item),
      [mutation],
    );

    return {
      ...query,
      entities: query.data,
      persistEntity,
      loading: query.isLoading,
      mutation,
    };
  },
  { hookName: 'useSyncPersistedEntities' },
);

export default useSyncPersistedEntities;
